/* iceberg-ui */
import { Card, CardBody } from '@auviknetworks/iceberg-ui';

/* MUI */
import { Grid } from '@mui/material';

/* Types */
import { User } from 'application/Shared/types/user';
import { useUserContext } from 'objects/User';

/* React */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

/* Internal Components */
import { AppsList } from './AppsList';
import { SaasOpsActionForm } from './SaasOpsActionForm';
import { SkippedAppsModal } from './SkippedAppsModal';
import {
    AllowedActions,
    SaasOpsActionItemStatus,
    SaasOpsActionItemsState,
    SaasOpsActionStatus
} from './types';

interface SaasOpsActionListAndFormProps {
    connectionInfo?: {
        displayName: string;
        image: string;
        integrationMessage?: string;
        name: string;
        offboardContent?: string;
    };
    action: AllowedActions;
    saasOpsActionItemsState: SaasOpsActionItemsState;
    setSaasOpsActionItemsState: React.Dispatch<
        React.SetStateAction<SaasOpsActionItemsState>
    >;
    currentAppId: string;
    overallSaasOpsActionStatus: SaasOpsActionStatus;
    setCurrentAppId: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Component that displays SaaS Ops action apps and form for a specific application and user.
 *
 * @component
 * @param {Object} props - Component props
 * @param {SaasOpsActionStatus} props.overallSaasOpsActionStatus - Overall status of the SaaS ops action
 * @param {SaasOpsActionItemsState} props.saasOpsActionItemsState - Current state of action items
 * @param {React.Dispatch<React.SetStateAction<SaasOpsActionItemsState>>} props.setSaasOpsActionItemsState - State setter for action item state
 *
 * @returns {React.ReactElement} A card containing an apps list, a form (based on the selected action) and skipped apps modal
 *
 * @example
 * <SaasOpsActionListAndForm
 *   action={WorkatoActions.offboard}
 *   overallSaasOpsActionStatus={SaasOpsActionStatus.InProgress}
 *   saasOpsActionItemsState={saasOpsActionItemsState}
 *   setSaasOpsActionItemsState={setSaasOpsActionItemsState}
 * />
 */
export const SaasOpsActionListAndForm = ({
    action,
    currentAppId,
    overallSaasOpsActionStatus,
    saasOpsActionItemsState,
    setSaasOpsActionItemsState,
    setCurrentAppId
}: SaasOpsActionListAndFormProps): React.ReactElement => {
    const [, setSearchParams] = useSearchParams();

    const { user: userDetails } = useUserContext();

    const [openSkippedAppsModal, setOpenSkippedAppsModal] =
        useState<boolean>(false);

    useEffect(() => {
        // If a user skips all apps and then selects `Remove` from the confirmation modal,
        // let's put them back on the app selection page.
        const allAppsRemoved = saasOpsActionItemsState.every(
            (appState) => appState.status === SaasOpsActionItemStatus.Removed
        );

        if (allAppsRemoved) {
            setSearchParams((prevSearchParams) => {
                prevSearchParams.delete('applicationId');
                return prevSearchParams;
            });
        }
    }, [saasOpsActionItemsState, setSearchParams]);

    const currentAppState = useMemo(
        () =>
            saasOpsActionItemsState.find(
                (appState) => appState.applicationId === currentAppId
            ),
        [saasOpsActionItemsState, currentAppId]
    );

    const goToSelectedApp = (applicationId: string) => {
        setCurrentAppId(applicationId);

        setSaasOpsActionItemsState((prevSaasOpsActionItemsState) => {
            const targetAppIndex = prevSaasOpsActionItemsState.findIndex(
                (appState) => appState.applicationId === applicationId
            );

            return prevSaasOpsActionItemsState.map((appState, index) => {
                // Mark the target app as the current app
                if (appState.applicationId === applicationId) {
                    return appState;
                }

                // If an app is not marked as Completed or Removed
                // AND it appears BEFORE the selected app in the list
                if (
                    ![
                        SaasOpsActionItemStatus.Completed,
                        SaasOpsActionItemStatus.Removed
                    ].includes(appState.status) &&
                    index < targetAppIndex
                ) {
                    // mark it as Skipped.
                    return {
                        ...appState,
                        status: SaasOpsActionItemStatus.Skipped
                    };
                }

                // Otherwise, leave them unchanged
                return appState;
            });
        });
    };

    const updateAppStatus = (status: SaasOpsActionItemStatus) => {
        setSaasOpsActionItemsState((prevSaasOpsActionItemsState) => {
            const currentIndex = prevSaasOpsActionItemsState.findIndex(
                (appState) => appState.applicationId === currentAppId
            );

            if (currentIndex !== -1) {
                const updated = [...prevSaasOpsActionItemsState];
                updated[currentIndex].status = status;

                return updated;
            }

            return prevSaasOpsActionItemsState;
        });
    };

    const goToNextApp = (
        updatedSaasOpsActionItemsState?: SaasOpsActionItemsState
    ) => {
        // When removing an app, the recipeAppState gets updated, but that state is stale if `goToNextApp`
        // gets called from there. So, we can pass the updated state when necessary into this function.
        const saasOpsActionItemsStateToUse =
            updatedSaasOpsActionItemsState || saasOpsActionItemsState;

        const currentIndex = saasOpsActionItemsStateToUse.findIndex(
            (appState) => appState.applicationId === currentAppId
        );

        // find the next app whose index > currentIndex AND is not removed OR completed
        if (currentIndex !== -1) {
            const nextApp = saasOpsActionItemsStateToUse.find(
                (appState, index) =>
                    index > currentIndex &&
                    ![
                        SaasOpsActionItemStatus.Removed,
                        SaasOpsActionItemStatus.Completed
                    ].includes(appState.status)
            );
            if (nextApp) {
                setCurrentAppId(nextApp.applicationId);
                return;
            }
        }

        // When there are skipped apps at the end, we need to show a confirmation modal to either
        // go back to the first skipped app or complete the process.
        const skippedOrPendingApp = saasOpsActionItemsStateToUse.find(
            (appState) =>
                [
                    SaasOpsActionItemStatus.Skipped,
                    SaasOpsActionItemStatus.Pending
                ].includes(appState.status)
        );

        if (skippedOrPendingApp) {
            setOpenSkippedAppsModal(true);
            return;
        }

        const nonRemovedAppState = saasOpsActionItemsStateToUse.filter(
            (appState) => appState.status !== SaasOpsActionItemStatus.Removed
        );

        // otherwise, just set the first non-removed app as the current app.
        if (nonRemovedAppState[0]?.applicationId) {
            setCurrentAppId(nonRemovedAppState[0].applicationId);
        }
    };

    const removeApp = (applicationId: string) => {
        const targetAppIndex = saasOpsActionItemsState.findIndex(
            (appState) => appState.applicationId === applicationId
        );

        if (targetAppIndex !== -1) {
            const updatedTenantAppState = [...saasOpsActionItemsState];
            // We have the updateAppStatus function that would handle this, but then we don't get the updated state object back to pass to goToNextApp.
            // Maybe we could move this logic to a `useEffect` based on state change?
            updatedTenantAppState[targetAppIndex].status =
                SaasOpsActionItemStatus.Removed;
            setSaasOpsActionItemsState(updatedTenantAppState);

            if (applicationId === currentAppId) {
                goToNextApp(updatedTenantAppState);
            }
        }
    };

    const reviewAppLater = () => {
        updateAppStatus(SaasOpsActionItemStatus.Skipped);
        goToNextApp();
    };

    const markAppAsCompleted = () => {
        updateAppStatus(SaasOpsActionItemStatus.Completed);
    };

    const markAppAsError = () => {
        updateAppStatus(SaasOpsActionItemStatus.Error);
    };

    const singleAppDisplay =
        saasOpsActionItemsState.filter(
            (appState) => appState.status !== SaasOpsActionItemStatus.Removed
        ).length === 1;

    return (
        <Card data-testid="saasOpsActionListAndForm">
            <CardBody p="0">
                <Grid container flexWrap="nowrap" position="relative">
                    {!!saasOpsActionItemsState?.length && (
                        <>
                            <AppsList
                                saasOpsActionItemsState={
                                    saasOpsActionItemsState
                                }
                                goToSelectedApp={goToSelectedApp}
                                currentAppId={currentAppId}
                                removeApp={removeApp}
                            />

                            {currentAppState && (
                                <SaasOpsActionForm
                                    key={currentAppState?.applicationId}
                                    action={action}
                                    appState={currentAppState!}
                                    goToNextApp={() => goToNextApp()}
                                    markAppAsCompleted={markAppAsCompleted}
                                    markAppAsError={markAppAsError}
                                    overallSaasOpsActionStatus={
                                        overallSaasOpsActionStatus
                                    }
                                    reviewAppLater={reviewAppLater}
                                    removeApp={removeApp}
                                    singleAppDisplay={singleAppDisplay}
                                    userDetails={userDetails! as Partial<User>}
                                />
                            )}
                        </>
                    )}
                </Grid>
            </CardBody>
            {openSkippedAppsModal && (
                <SkippedAppsModal
                    goToNextApp={goToNextApp}
                    saasOpsAction={action}
                    setCurrentAppId={setCurrentAppId}
                    setOpenSkippedAppsModal={setOpenSkippedAppsModal}
                    setSaasOpsActionItemsState={setSaasOpsActionItemsState}
                    skippedApps={saasOpsActionItemsState.filter((appState) =>
                        [
                            SaasOpsActionItemStatus.Skipped,
                            SaasOpsActionItemStatus.Pending
                        ].includes(appState.status)
                    )}
                />
            )}
        </Card>
    );
};
